import { useCallback, useRef, useMemo, useState } from 'react';
import { Button, Space, Tag, Modal, message, Select, Tooltip } from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  EyeOutlined,
  EditOutlined,
  PlayCircleOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { DataGrid, type DataGridHandle } from '@/components/DataGrid';
import type { SearchParams } from '@/api/types';
import {
  listDeduplicationRules,
  deleteDeduplicationRule,
  runDeduplicationJob,
  type DeduplicationRule,
} from '@/api/deduplicationApi';
import { DataManagementModules } from '@/api/dataManagementApi';
import { useAuth } from '@/hooks/useAuth';
import DataManagementNav from './DataManagementNav';

export default function DeduplicationRuleListPage() {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const gridRef = useRef<DataGridHandle>(null);

  const [moduleFilter, setModuleFilter] = useState<string | undefined>();
  const [activeFilter, setActiveFilter] = useState<string | undefined>();

  const canRead = hasPermission('data_management', 'read');
  const canWrite = hasPermission('data_management', 'write');
  const canDelete = hasPermission('data_management', 'delete');

  const fetchData = useCallback(
    (params: SearchParams) => {
      const filters: Record<string, any> = { ...params };
      if (moduleFilter) filters.target_module = moduleFilter;
      if (activeFilter) filters.is_active = activeFilter;
      return listDeduplicationRules(filters);
    },
    [moduleFilter, activeFilter]
  );

  const handleDelete = useCallback(
    (id: string) => {
      Modal.confirm({
        title: t('app.confirm'),
        content: t('data_management.dedup.rule.delete_confirm'),
        okText: t('app.yes'),
        cancelText: t('app.no'),
        okType: 'danger',
        onOk: async () => {
          try {
            await deleteDeduplicationRule(id);
            message.success(t('data_management.dedup.rule.delete_success'));
            gridRef.current?.reload();
          } catch {
            message.error(t('app.error'));
          }
        },
      });
    },
    [t]
  );

  const handleRun = useCallback(
    (id: string) => {
      Modal.confirm({
        title: t('app.confirm'),
        content: t('data_management.dedup.rule.run'),
        okText: t('app.yes'),
        cancelText: t('app.no'),
        onOk: async () => {
          try {
            await runDeduplicationJob(id);
            message.success(t('data_management.dedup.job.run_success'));
            navigate('/data-management/dedup-jobs');
          } catch {
            message.error(t('app.error'));
          }
        },
      });
    },
    [t, navigate]
  );

  const columns: ColumnsType<DeduplicationRule> = useMemo(
    () => [
      {
        title: t('data_management.dedup.rule.field.name'),
        dataIndex: 'name',
        key: 'name',
        ellipsis: true,
        sorter: true,
      },
      {
        title: t('data_management.dedup.rule.field.target_module'),
        dataIndex: 'target_module',
        key: 'target_module',
        width: 120,
        render: (v: string) => t(`data_management.import.modules.${v}`, v),
      },
      {
        title: t('data_management.dedup.rule.field.match_strategy'),
        dataIndex: 'match_strategy',
        key: 'match_strategy',
        width: 140,
        render: (v: string) => t(`data_management.dedup.rule.match_strategies.${v}`, v),
      },
      {
        title: t('data_management.dedup.rule.field.merge_strategy'),
        dataIndex: 'merge_strategy',
        key: 'merge_strategy',
        width: 140,
        render: (v: string) => t(`data_management.dedup.rule.merge_strategies.${v}`, v),
      },
      {
        title: t('data_management.dedup.rule.field.is_active'),
        dataIndex: 'is_active',
        key: 'is_active',
        width: 80,
        render: (v: boolean) => (
          <Tag color={v ? 'green' : 'red'}>
            {v ? t('app.yes') : t('app.no')}
          </Tag>
        ),
      },
      {
        title: t('data_management.dedup.rule.field.total_duplicates_found'),
        dataIndex: 'total_duplicates_found',
        key: 'total_duplicates_found',
        width: 100,
        render: (v: number) => v > 0 ? v.toLocaleString() : '-',
      },
      {
        title: t('data_management.dedup.rule.field.total_merged'),
        dataIndex: 'total_merged',
        key: 'total_merged',
        width: 90,
        render: (v: number) => v > 0 ? v.toLocaleString() : '-',
      },
      {
        title: t('data_management.dedup.rule.field.last_run_at'),
        dataIndex: 'last_run_at',
        key: 'last_run_at',
        width: 160,
        sorter: true,
        render: (v: string | null) => v ? new Date(v).toLocaleString() : '-',
      },
      {
        title: t('app.actions'),
        key: 'actions',
        width: 170,
        render: (_: unknown, record: DeduplicationRule) => (
          <Space>
            {canRead && (
              <Tooltip title={t('app.view')}>
                <Button
                  type="text"
                  size="small"
                  icon={<EyeOutlined />}
                  onClick={() => navigate(`/data-management/dedup-rules/${record.id}`)}
                />
              </Tooltip>
            )}
            {canWrite && (
              <Tooltip title={t('app.edit')}>
                <Button
                  type="text"
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => navigate(`/data-management/dedup-rules/${record.id}/edit`)}
                />
              </Tooltip>
            )}
            {canWrite && record.is_active && (
              <Tooltip title={t('data_management.dedup.rule.run')}>
                <Button
                  type="text"
                  size="small"
                  icon={<PlayCircleOutlined />}
                  onClick={() => handleRun(record.id)}
                />
              </Tooltip>
            )}
            {canDelete && (
              <Tooltip title={t('app.delete')}>
                <Button
                  type="text"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => handleDelete(record.id)}
                />
              </Tooltip>
            )}
          </Space>
        ),
      },
    ],
    [t, canRead, canWrite, canDelete, navigate, handleDelete, handleRun]
  );

  return (
    <div>
      <DataManagementNav />
      <Space style={{ marginBottom: 16 }}>
        <Select
          allowClear
          placeholder={t('data_management.dedup.rule.field.target_module')}
          style={{ width: 140 }}
          value={moduleFilter}
          onChange={setModuleFilter}
          options={DataManagementModules.map((m) => ({
            value: m,
            label: t(`data_management.import.modules.${m}`),
          }))}
        />
        <Select
          allowClear
          placeholder={t('data_management.dedup.rule.field.is_active')}
          style={{ width: 120 }}
          value={activeFilter}
          onChange={setActiveFilter}
          options={[
            { value: 'true', label: t('app.yes') },
            { value: 'false', label: t('app.no') },
          ]}
        />
        {canWrite && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate('/data-management/dedup-rules/new')}
          >
            {t('data_management.dedup.rule.new')}
          </Button>
        )}
      </Space>
      <DataGrid
        ref={gridRef}
        columns={columns}
        fetchData={fetchData}
        rowKey="id"
      />
    </div>
  );
}
